// Document-order helpers for the section outline. Section numbers must sort
// NUMERICALLY ("10.2" follows "9", not "1"), and spine sections the doctype
// skeleton didn't claim (e.g. a drafted section whose number doesn't line up
// with a skeleton entry) must land at their numeric position — NOT be dumped at
// the front, which is what scrambled the outline.
//
// CTD numbers are ALPHANUMERIC: `3.2.P.1`, `3.2.S.4.4`. A segment is a number
// or a letter, so the key is parsed per segment — treating "all-numeric" as the
// only sortable shape made every row of a leaf-scoped CMC outline tie (the same
// wrong assumption the renderer's `_RE_TOP_HEADING` once made; one assumption,
// two places). Front matter is anything that does not START with a digit
// ("TITLE", ""): it leads, unranked, exactly as before.

export interface OrderableRow {
  number: string;
}

/** A section-number segment: `3` orders numerically, `P` by CTD convention. */
type Segment = number | string;

/** eCTD Module-3 letter order: Substance, Product, Appendices, Regional —
 *  convention, not alphabet (alphabetical would put Appendices first). Letters
 *  outside the convention fall back to alphabetical, after these. */
const CTD_LETTER_RANK: Record<string, number> = { S: 1, P: 2, A: 3, R: 4 };

/** Sort key: bucket 0 = front matter (no leading digit — title page, ""),
 *  unranked and first; bucket 1 = a section number, split into segments. */
export function orderKey(number: string): [number, Segment[]] {
  const raw = (number || "").trim();
  if (!/^\d/.test(raw)) return [0, []];
  const parts: Segment[] = raw
    .replace(/\.$/, "")
    .split(".")
    .map((p) => (/^\d+$/.test(p) ? Number.parseInt(p, 10) : p.toUpperCase()));
  return [1, parts];
}

/** One segment against another; `undefined` = the shorter number ran out, and
 *  a prefix sorts before its extensions (`3.2.P` before `3.2.P.1`). */
function compareSegment(a: Segment | undefined, b: Segment | undefined): number {
  if (a === undefined || b === undefined) return a === undefined ? (b === undefined ? 0 : -1) : 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "string" && typeof b === "string") {
    const ra = CTD_LETTER_RANK[a];
    const rb = CTD_LETTER_RANK[b];
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined || rb !== undefined) return ra !== undefined ? -1 : 1;
    return a < b ? -1 : a > b ? 1 : 0;
  }
  return typeof a === "number" ? -1 : 1; // numeric before lettered at the same depth
}

/** Compare two rows by natural section-number order. */
export function compareByNumber(a: OrderableRow, b: OrderableRow): number {
  const [ka, pa] = orderKey(a.number);
  const [kb, pb] = orderKey(b.number);
  if (ka !== kb) return ka - kb;
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = compareSegment(pa[i], pb[i]);
    if (d !== 0) return d;
  }
  return 0;
}

/** Insert `unclaimed` rows into `ordered` (already in document order) at their
 *  numeric section-number position. `ordered`'s existing order is preserved
 *  exactly — the skeleton stays authoritative; only the extra rows are placed. */
export function placeUnclaimed<T extends OrderableRow>(ordered: T[], unclaimed: T[]): T[] {
  const out = [...ordered];
  for (const u of unclaimed) {
    let idx = out.findIndex((r) => compareByNumber(u, r) < 0);
    if (idx < 0) idx = out.length;
    out.splice(idx, 0, u);
  }
  return out;
}
